/**
 * 前端和星海服务之间的接口。两边都从这里取类型和路径，改动时两边一起改。
 * 数据模型见 docs/design.md §5；这里是实际用到的子集。
 */
import type { LocalDate } from './dates.ts';

export type MemberTint = 'gold' | 'rose';
export type Shine = 1 | 2 | 3 | 4 | 5;

/** 我们的星海 */
export interface SpaceDto {
  id: string;
  name: string;
  /** 在一起的那天，螺线从这里开始 */
  since: LocalDate;
  /** 入场爱心炸开以后显示的那段话；没写时前端用默认的「在一起的第 N 天 / 已经点亮了 M 颗星」 */
  introText?: string;
  /** 那段话停留几秒再化成星尘飘走；0 是不显示 */
  introHold: number;
  /** 写前互盲（双面翻牌，ADR-0011）：写下自己这一面之前看不到 Ta 写的。老的离线快照里没有这一项 */
  blindPerspectives?: boolean;
}

export interface MemberDto {
  id: string;
  displayName: string;
  /** 星芒的颜色：先加入的是淡金，后加入的是玫瑰 */
  tint: MemberTint;
}

export interface DeviceDto {
  id: string;
  memberId: string;
  label: string;
  createdAt: string;
  lastSeenAt?: string;
  /** 是不是发起这次请求的设备 */
  current?: boolean;
}

export interface PlaceDto {
  name: string;
  lat?: number;
  lng?: number;
}

/** 一段回忆：一天或一件事，故事写在这里 */
export interface MemoryDto {
  id: string;
  date: LocalDate;
  endDate?: LocalDate;
  title?: string;
  story?: string;
  place?: PlaceDto;
  coverPhotoId?: string;
  shine: Shine;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

/** 一张照片，也就是一颗星 */
export interface PhotoDto {
  id: string;
  memoryId: string;
  /** 拍摄的当地日期，决定星的位置 */
  date: LocalDate;
  /** EXIF 拍摄时间，知道时区时带偏移，如 2024-05-20T19:30:12+08:00 */
  takenAt?: string;
  lat?: number;
  lng?: number;
  /** 摆正方向之后的尺寸 */
  width: number;
  height: number;
  /** 主色 #rrggbb，也是星的颜色 */
  color: string;
  /** ThumbHash（base64），缩略图到达前先显示 */
  thumbhash?: string;
  /** 缩略图是否已生成；原图解不开（比如服务端还不能解 HEIC 且没有显示版）时为 false */
  thumbs: boolean;
  mime: string;
  bytes: number;
  caption?: string;
  createdBy: string;
  createdAt: string;
}

/**
 * 一张照片上一个人的视角（双面翻牌，ADR-0011）。上传的人那面是正面（A 面），另一个人那面是背面（B 面），
 * 每人只能写自己那面。写前互盲开着、你还没写这张照片的那一面时，Ta 那面只有 hidden 和时间，没有 text。
 */
export interface PerspectiveDto {
  photoId: string;
  memberId: string;
  text?: string;
  hidden?: true;
  createdAt: string;
  updatedAt: string;
}

/**
 * 写、改、清空自己那一面：text 是 null 或空白时清空。
 * base 是打开编辑时自己那面的 updatedAt（还没写过是 null）：同一个人在两台设备上同时改，后存的那台退回 409
 */
export interface PerspectivePatch {
  text: string | null;
  base: string | null;
}

/** 写完以后这张照片的两面（按你能看到的） */
export interface PerspectivesResponse {
  photoId: string;
  perspectives: PerspectiveDto[];
}

/** 写视角时的 409：你在另一台设备上刚改过这一面，附上最新的（清空了是 null） */
export interface PerspectiveConflictBody extends ApiErrorBody {
  perspective: PerspectiveDto | null;
}

/**
 * 时间胶囊的两种形态（ADR-0011）：
 * - surprise（秘密惊喜）：封存的人随时完整可见，能改信、调日子、撤回；另一个人到日子之前只有倒计时
 * - pact（共同契约）：两个人都蒙着；封存的人可以确认后偷看，偷看会留下两个人都看得见的印记
 */
export type CapsuleKind = 'surprise' | 'pact';

/**
 * 一颗时间胶囊（ADR-0011）：星茧放在解封那天的位置。服务端按看的人给：
 * 你能看里面（拆开过；或者你是封存的人，秘密惊喜、或者共同契约偷看过）才有 letter 和 photo，不然只有外壳。
 * 里面的照片不进照片列表、不成为单独的星：拆开以后由带星环的这颗星打开。
 */
export interface CapsuleDto {
  id: string;
  kind: CapsuleKind;
  /** 星茧在螺线上的哪一天（解封那天，封存的人那里的当地日期） */
  date: LocalDate;
  /** 解封的时刻；到了以后才能拆 */
  unlockAt: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** 已经亲手拆开过的人 */
  openedBy: string[];
  /** 共同契约：封存的人偷看过几次、最后一次是什么时候（两个人都看得见） */
  peekCount: number;
  peekedAt?: string;
  hasLetter: boolean;
  hasPhoto: boolean;
  /** 你能看里面时才有 */
  letter?: string;
  photo?: PhotoDto;
}

export interface CapsuleResponse {
  capsule: CapsuleDto;
}

/** 秘密惊喜里封存的人改信、调日子；没带的不动，letter 是 null 或空白时去掉信（还得有照片） */
export interface CapsulePatch {
  letter?: string | null;
  unlockAt?: string;
  date?: LocalDate;
}

/**
 * 入场暗号（ADR-0011）：留给 Ta 的一句话。Ta 下次打开星海时，入场的那段话换成它；Ta 看到以后，它是留的那天的一个足迹。
 * 每人最多一条 Ta 还没看到的，再写就是改这一条。两个人都拿得到全文：本来就是写给 Ta 的，只是等 Ta 入场时才显示。
 */
export interface NoteDto {
  id: string;
  /** 留的人、收的人（成员 id） */
  from: string;
  to: string;
  text: string;
  /** 留的那天（留的人那里的当地日期）：看过以后足迹落在螺线的这一天 */
  date: LocalDate;
  createdAt: string;
  updatedAt: string;
  /** Ta 在入场时看到的那一刻；还没看到时没有 */
  readAt?: string;
}

/** 写、改给 Ta 的那句（Ta 还没看到的那条）；text 是 null 或空白时撤回。date 是这台设备那里的今天 */
export interface NoteRequest {
  text: string | null;
  date?: LocalDate;
}

export interface NoteResponse {
  /** 撤回以后是 null */
  note: NoteDto | null;
}

/** 回收站里的照片（design §4.7「删除」）：满 LIMITS.trashDays 天服务端会把它永久删除 */
export interface TrashedPhotoDto extends PhotoDto {
  deletedAt: string;
  /** 到这个时间服务端会把原图、缩略图和记录一起删掉 */
  purgeAt: string;
}

export type ThumbSize = 't128' | 't512' | 't2048';
export type PhotoVariant = ThumbSize | 'original';
export const THUMB_SIZES: Readonly<Record<ThumbSize, number>> = { t128: 128, t512: 512, t2048: 2048 };
export const PHOTO_VARIANTS: readonly PhotoVariant[] = ['t128', 't512', 't2048', 'original'];

export interface HealthResponse {
  ok: true;
  /** 星海是否已经建好 */
  setup: boolean;
  version: string;
}

/** 首次设置：用服务启动时打印的一次性设置码建星海 */
export interface SetupRequest {
  code: string;
  spaceName: string;
  since: LocalDate;
  displayName: string;
  deviceLabel: string;
}

/** 用邀请码加入。邀请 Ta 时要填称呼；给自己加设备时不用 */
export interface PairRequest {
  code: string;
  displayName?: string;
  deviceLabel: string;
}

export interface MeResponse {
  space: SpaceDto;
  member: MemberDto;
  device: DeviceDto;
}

export interface SessionResponse extends MeResponse {
  /** 设备令牌，只在这一次返回，服务端只存哈希 */
  token: string;
}

export type InviteKind = 'member' | 'device';

export interface InviteRequest {
  kind: InviteKind;
}

export interface InviteResponse {
  code: string;
  kind: InviteKind;
  expiresAt: string;
}

export interface SnapshotResponse {
  /** 这份快照对应的变更序号，订阅时从这里接着收 */
  seq: number;
  space: SpaceDto;
  members: MemberDto[];
  memories: MemoryDto[];
  /** 按添加顺序（createdAt，再按 id） */
  photos: PhotoDto[];
  /** 照片上的视角，按你能看到的（ADR-0011）。老的离线快照里没有这一项 */
  perspectives?: PerspectiveDto[];
  /** 时间胶囊，按你能看到的（ADR-0011）。老的离线快照里没有这一项 */
  capsules?: CapsuleDto[];
  /** 入场暗号，两个人留的都在（ADR-0011）。老的离线快照里没有这一项 */
  notes?: NoteDto[];
}

interface ChangeBase {
  seq: number;
  at: string;
  /** 谁做的改动 */
  by?: string;
}

export type Change =
  | (ChangeBase & { kind: 'photo.added'; photo: PhotoDto; memory: MemoryDto })
  | (ChangeBase & { kind: 'member.joined'; member: MemberDto })
  | (ChangeBase & { kind: 'memory.updated'; memory: MemoryDto })
  | (ChangeBase & { kind: 'space.updated'; space: SpaceDto })
  /** 放进了回收站：星熄灭，照片还在服务端 */
  | (ChangeBase & { kind: 'photo.deleted'; photo: TrashedPhotoDto })
  /** 从回收站恢复了（也可能是有人把回收站里的那张又传了一次） */
  | (ChangeBase & { kind: 'photo.restored'; photo: PhotoDto; memory: MemoryDto })
  /** 永久删除了（满 30 天，或者有人在回收站里点了永久删除）；没有 by 时是服务端自己清的 */
  | (ChangeBase & { kind: 'photo.purged'; photoId: string })
  /** 一张照片的视角变了：两个人各收一条，各是自己能看到的样子（ADR-0011）；by 是写的人 */
  | (ChangeBase & { kind: 'perspective.updated'; photoId: string; perspectives: PerspectiveDto[] })
  /** 写前互盲开了或关了：所有视角按你现在能看到的重发一遍，整个换掉 */
  | (ChangeBase & { kind: 'perspective.reset'; perspectives: PerspectiveDto[] })
  /** 时间胶囊封存了、改了、偷看了、有人拆开了：两个人各收一条，各是自己能看到的样子 */
  | (ChangeBase & { kind: 'capsule.updated'; capsule: CapsuleDto })
  /** 封存的人撤回了（转成普通照片时另有一条 photo.added） */
  | (ChangeBase & { kind: 'capsule.removed'; capsuleId: string })
  /** 留了、改了给 Ta 的入场暗号 */
  | (ChangeBase & { kind: 'note.updated'; note: NoteDto })
  /** Ta 在入场时看到了：从这时起它是那天的足迹 */
  | (ChangeBase & { kind: 'note.read'; note: NoteDto })
  /** Ta 看到之前，留的人撤回了 */
  | (ChangeBase & { kind: 'note.removed'; noteId: string });

export type ChangeKind = Change['kind'];

export interface ChangesResponse {
  seq: number;
  changes: Change[];
}

export interface UploadResponse {
  photo: PhotoDto;
  memory: MemoryDto;
  /** 同一张照片（内容哈希相同）已经在星海里了，这次没有新增 */
  duplicate: boolean;
  /** 同一张照片在回收站里，这次是把它恢复了 */
  restored: boolean;
}

export interface TrashResponse {
  /** 按放进回收站的时间，新的在前 */
  photos: TrashedPhotoDto[];
}

/** 放进回收站的回应；已经在回收站里时照样成功 */
export interface TrashedPhotoResponse {
  photo: TrashedPhotoDto;
}

/** 从回收站恢复的回应 */
export interface RestoreResponse {
  photo: PhotoDto;
  memory: MemoryDto;
}

export interface DevicesResponse {
  devices: DeviceDto[];
}

/**
 * 改一段回忆：只带要改的字段，没带的不动；null 或空字符串是清空。
 * 两个人可能同时在改：base 是打开编辑时看到的 updatedAt，服务端的版本已经不是它（Ta 刚改过）时
 * 返回 409 和最新的回忆（MemoryConflictBody），不覆盖，由改的人看过 Ta 改了什么再决定。
 */
export interface MemoryPatch {
  base: string;
  title?: string | null;
  story?: string | null;
  place?: PlaceDto | null;
  shine?: Shine;
}

export interface MemoryResponse {
  memory: MemoryDto;
}

/** 改星海的名字、在一起的日子、入场的那段话、写前互盲；没带的不动。introText 是 null 或空字符串时回到默认文案 */
export interface SpacePatch {
  name?: string;
  since?: LocalDate;
  introText?: string | null;
  introHold?: number;
  blindPerspectives?: boolean;
}

export interface SpaceResponse {
  space: SpaceDto;
  /**
   * 这次改动的变更序号（什么都没变时是当前的序号）。星海没有版本号，推送来的 space.updated 和这个回应
   * 谁先到不一定，按序号比先后，免得晚到的旧版本盖掉新的
   */
  seq: number;
}

export interface ApiErrorBody {
  error: ApiErrorCode;
  message: string;
}

/** 改回忆时的 409：你编辑的时候 Ta 改过了，附上最新的版本 */
export interface MemoryConflictBody extends ApiErrorBody {
  memory: MemoryDto;
}

export type ApiErrorCode =
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'too_large'
  | 'unsupported'
  | 'rate_limited'
  | 'internal';

export const API = {
  health: '/api/health',
  setup: '/api/setup',
  pair: '/api/pair',
  me: '/api/me',
  invites: '/api/invites',
  devices: '/api/devices',
  snapshot: '/api/snapshot',
  changes: '/api/changes',
  events: '/api/events',
  photos: '/api/photos',
  memories: '/api/memories',
  space: '/api/space',
  trash: '/api/trash',
  capsules: '/api/capsules',
  notes: '/api/notes',
  /** PUT：写、改、撤回留给 Ta 的那句入场暗号 */
  myNote: '/api/notes/mine',
} as const;

export function photoPath(id: string, variant: PhotoVariant): string {
  return `${API.photos}/${encodeURIComponent(id)}/${variant}`;
}

export function memoryPath(id: string): string {
  return `${API.memories}/${encodeURIComponent(id)}`;
}

/** DELETE 是放进回收站 */
export function photoItemPath(id: string): string {
  return `${API.photos}/${encodeURIComponent(id)}`;
}

/** POST 是从回收站恢复 */
export function restorePath(id: string): string {
  return `${API.photos}/${encodeURIComponent(id)}/restore`;
}

/** DELETE 是永久删除（只能删回收站里的） */
export function trashItemPath(id: string): string {
  return `${API.trash}/${encodeURIComponent(id)}`;
}

/** PUT 是写、改、清空自己在这张照片上的视角 */
export function perspectivePath(photoId: string): string {
  return `${API.photos}/${encodeURIComponent(photoId)}/perspective`;
}

/**
 * 一颗时间胶囊：PATCH 改（秘密惊喜），DELETE 撤回（?photo=keep 时照片转成普通照片，否则一起删掉）；
 * action 是 'open'（亲手拆开）或 'peek'（共同契约里偷看）时 POST
 */
export function capsulePath(id: string, action?: 'open' | 'peek'): string {
  return `${API.capsules}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`;
}

/** POST：收的人在入场时看到了这句暗号 */
export function noteReadPath(id: string): string {
  return `${API.notes}/${encodeURIComponent(id)}/read`;
}

/** 封存时间胶囊的表单字段；照片的字段同 UPLOAD_FIELD */
export const CAPSULE_FIELD = {
  kind: 'kind',
  letter: 'letter',
  unlockAt: 'unlockAt',
  date: 'date',
} as const;

/** 上传接口的表单字段 */
export const UPLOAD_FIELD = {
  /** 原图，必填，原样保存 */
  original: 'original',
  /** 显示版（JPEG / WebP），选填。服务端解不开原图（如 HEIC）时用它生成缩略图 */
  display: 'display',
  /** 读不到 EXIF 时用的日期（前端按文件修改时间、本机时区算） */
  fallbackDate: 'fallbackDate',
  fallbackTakenAt: 'fallbackTakenAt',
} as const;

export const LIMITS = {
  uploadBytes: 64 * 1024 * 1024,
  nameLength: 24,
  spaceNameLength: 32,
  deviceLabelLength: 40,
  /** 回忆的标题、地点、故事（按字数，不是字节） */
  titleLength: 30,
  placeLength: 40,
  storyLength: 5000,
  /** 删了的照片在回收站里留多少天，之后服务端永久删除 */
  trashDays: 30,
  /** 入场那段话：最多多少字、几行，停留几秒（默认 3 秒，最长 10 秒） */
  introTextLength: 120,
  introLines: 4,
  introHold: 3,
  introHoldMax: 10,
  /** 照片上一个人的视角（双面翻牌） */
  perspectiveLength: 500,
  /** 时间胶囊里的信；最远能封到多少年以后 */
  letterLength: 5000,
  capsuleYears: 50,
  /** 入场暗号：它会代替入场的那段话，所以一样长 */
  noteLength: 120,
  noteLines: 4,
  /** 一个星海最多两个人 */
  members: 2,
  inviteHours: 24,
} as const;

/** 设置码、邀请码用的字母表：去掉了容易看错的 0 O 1 I L */
export const CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

/** 用户输入的码：去掉空格和连字符，转大写 */
export function normalizeCode(input: string): string {
  return input.toUpperCase().replace(/[^0-9A-Z]/g, '');
}

/** 显示用：每 4 或 5 位加一个连字符 */
export function formatCode(code: string): string {
  const n = code.length % 5 === 0 ? 5 : 4;
  return code.match(new RegExp(`.{1,${n}}`, 'g'))?.join('-') ?? code;
}
